import { observer } from "mobx-react-lite";
import { useSearchParams } from "react-router-dom";
import { motion } from "motion/react";
import { FC, useCallback, useMemo } from "react";
import styled from "@emotion/styled";
import { OAuthConsentPageContent } from "@/pages/oauth-consent/oauth-consent-page-content";
import { useAppStore } from "@/store";
import { oauthModule } from "@/modules/oauth";
import { windowModule } from "@/modules/window";

export const OAuthConsentPage: FC = observer(() => {
  const [search] = useSearchParams();
  const { store } = useAppStore();

  const redirectUri = search.get("redirect_uri");
  const clientId = search.get("client_id");
  const state = search.get("state");
  const scopeStr = search.get("scope");
  const responseType = search.get("response_type");

  /** TODO: Make this server-provided. */
  const appName = "Zapier";

  const account = store.account.myAccount;

  const accountInfo = useMemo(
    () => ({
      profileDisplayName: account.profileDisplayName,
      profileEmailAddress: account.profileEmailAddress,
      profilePhotoUrl: account.profilePhotoUrl,
    }),
    [account.profileDisplayName, account.profileEmailAddress, account.profilePhotoUrl]
  );

  const handleApprove = useCallback(async () => {
    if (!redirectUri || !clientId || !state || !scopeStr) {
      return;
    }

    const result = await oauthModule.approveOAuthConsent({
      clientId,
      redirectUri,
      scope: scopeStr,
      state,
    });

    if (result?.redirect_url) {
      await windowModule.navigateToExternal({ url: new URL(result.redirect_url) });
    }
  }, [clientId, redirectUri, scopeStr, state]);

  const handleDeny = useCallback(async () => {
    if (!redirectUri || !clientId || !state || !scopeStr) {
      return;
    }

    const result = await oauthModule.denyOAuthConsent({
      clientId,
      redirectUri,
      scope: scopeStr,
      state,
    });

    if (result?.redirect_url) {
      await windowModule.navigateToExternal({ url: new URL(result.redirect_url) });
    }
  }, [clientId, redirectUri, scopeStr, state]);

  return (
    <PageWrapper>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.1 }}>
        <OAuthConsentPageContent
          redirectUri={redirectUri}
          clientId={clientId}
          state={state}
          scopeStr={scopeStr}
          responseType={responseType}
          appName={appName}
          accountInfo={accountInfo}
          onApprove={handleApprove}
          onDeny={handleDeny}
        />
      </motion.div>
    </PageWrapper>
  );
});

const PageWrapper = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  -webkit-app-region: drag;
  z-index: 9999;
  background: ${({ theme }) => theme.colors.surface.pane};
`;
